import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { HttpClientModule } from '@angular/common/http';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { A11yModule } from '@angular/cdk/a11y';

// Components
import { HeaderComponent } from './components/header/header.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { SearchBarComponent } from './components/search-bar/search-bar.component';
import { LocationCardComponent } from './components/location-card/location-card.component';
import { GalleryGridComponent } from './components/gallery-grid/gallery-grid.component';
import { LoadingSpinnerComponent } from './components/loading-spinner/loading-spinner.component';
import { ModalComponent } from './components/modal/modal.component';
import { AiChatbotComponent } from './components/ai-chatbot/ai-chatbot.component';
import { DatePickerComponent } from './components/date-picker/date-picker.component';
import { ImageEditorComponent } from './components/image-editor/image-editor.component';
import { DynamicFieldComponent } from './components/dynamic-field/dynamic-field.component';
import { ConfirmDeleteDialogComponent } from './components/confirm-delete-dialog/confirm-delete-dialog.component';
import { CookieConsentComponent } from './components/cookie-consent/cookie-consent.component';
import { ToastContainerComponent } from './components/toast-container/toast-container.component';
import { TenantFooterGcfcComponent } from './components/tenant-footer-gcfc/tenant-footer-gcfc.component';
import { IdleWarningModalComponent } from './components/idle-warning-modal/idle-warning-modal.component';
import { PasswordNotSetNoticeComponent } from './components/password-not-set-notice/password-not-set-notice.component';
import { RegisterMessagesComponent } from './components/register-messages/register-messages.component';

// Directives
import { GooglePlacesAutocompleteDirective } from './directives/google-places-autocomplete.directive';
import { TooltipDirective } from './directives/tooltip.directive';
import { FloatingPanelDirective } from './directives/floating-panel.directive';
import { AutoAriaLabelDirective } from './directives/aria-label.directive';
import { ScrollableRegionDirective } from './directives/scrollable-region.directive';
import { AvatarFitDirective } from './directives/avatar-fit.directive';
import { NumericOnlyDirective } from './directives/numeric-only.directive';

// Pipes
import { UtcDatePipe } from './pipes/utc-date.pipe';
import { ExternalUrlPipe } from './pipes/external-url.pipe';

// NgModule-declared (not standalone) — still bundled as one unit for every
// consumer of SharedModule. Kept here because they're admin/editor-only and
// not needed by public tenant pages (see PublicSharedModule).
const COMPONENTS = [
  SidebarComponent,
  SearchBarComponent,
  LocationCardComponent,
  GalleryGridComponent,
  ModalComponent,
  AiChatbotComponent,
  ConfirmDeleteDialogComponent,
  CookieConsentComponent,
  ToastContainerComponent,
  TenantFooterGcfcComponent,
  IdleWarningModalComponent
];

// Standalone (converted 2026-08-24 for the public-bundle split — see
// PublicSharedModule): re-exported here via `imports`/`exports` so every
// existing SharedModule consumer (admin/locations/lookbooks/permits/auth)
// keeps working unchanged, while PublicModule can import just these few
// pieces directly instead of the whole SharedModule.
const STANDALONE_COMPONENTS = [
  HeaderComponent,
  LoadingSpinnerComponent,
  DatePickerComponent,
  ImageEditorComponent,
  DynamicFieldComponent,
  RegisterMessagesComponent,
  PasswordNotSetNoticeComponent
];

const STANDALONE_DIRECTIVES = [
  GooglePlacesAutocompleteDirective,
  TooltipDirective,
  FloatingPanelDirective,
  AvatarFitDirective,
  NumericOnlyDirective,
  AutoAriaLabelDirective,
  ScrollableRegionDirective
];

const PIPES = [
  UtcDatePipe,
  ExternalUrlPipe
];

@NgModule({
  declarations: [...COMPONENTS],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    HttpClientModule,
    DragDropModule,
    A11yModule,
    ...STANDALONE_COMPONENTS,
    ...STANDALONE_DIRECTIVES,
    ...PIPES
  ],
  exports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    HttpClientModule,
    DragDropModule,
    A11yModule,
    ...COMPONENTS,
    ...STANDALONE_COMPONENTS,
    ...STANDALONE_DIRECTIVES,
    ...PIPES
  ]
})
export class SharedModule { }
